import {Chart} from "primereact/chart";
import {SelectButton} from "primereact/selectbutton";
import {useEffect, useMemo} from "react";

import {formatIndex} from "@reva/shared/number-format";
import type {ChartData, ChartDataset, ChartOptions} from "chart.js";

import type {PoolThresholdTile} from "../../api/payload-types/pool-statistics";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {
    POOL_STATISTICS_MODES,
    selectCurrentPoolStatistics,
    selectPoolStatisticsError,
    selectPoolStatisticsLoading,
    selectPoolStatisticsMode,
} from "../../redux/poolStatisticsSlice";
import {changePoolStatisticsModeThunk, initializePoolStatisticsThunk} from "../../redux/poolStatisticsThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {selectDarkTheme} from "../../redux/themeSlice";
import {getChartStyle, getColor, getPrimaryBarColor} from "../../util/chartUtils";
import {capitalize} from "../../util/stringUtil";
import {buildStatisticsChartOptions, LegendItem, STATISTICS_CHART_BOX_CLASS, StatTile} from "./startPageWidgets";

/** Capitalizes the mode value for building its i18n key (e.g. "WEEKS" → "modeWeeks"). */
const modeI18nKey = (mode: string) => `start.pool.statistics.mode${capitalize(mode.toLowerCase())}`;

interface PoolStatisticsPanelProps {
    className?: string;
}

/**
 * #19113: Statistics above the POOL tab's ("Auftragspool") valuation list — a snapshot of the
 * current order pool (no time series, since REVA-ES has no owner/lifecycle event log). Two fixed
 * summary tiles (pool size, average age) plus three threshold tiles that depend on the selected
 * time-unit mode, and a bar chart of the age distribution with the average/median shown as text
 * markers. The mode switcher (Weeks / Months / Dynamic) mirrors the DONE tab's interval switcher and
 * lets the user "play" with the time unit — it drives both the threshold tiles and the chart x-axis.
 * Laid out 1:1 like the DONE tab's {@link SnapshotStatisticsPanel} so both areas have the same height.
 */
export default function PoolStatisticsPanel({className}: PoolStatisticsPanelProps) {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();

    const isDarkTheme = useRevaSelector(selectDarkTheme);
    const mode = useRevaSelector(selectPoolStatisticsMode);
    const statistics = useRevaSelector(selectCurrentPoolStatistics);
    const loading = useRevaSelector(selectPoolStatisticsLoading);
    const statisticsError = useRevaSelector(selectPoolStatisticsError);

    // Gemountet wird das Panel erst beim Aufklappen; der Thunk lädt nur beim ersten Mal.
    useEffect(() => {
        void dispatch(initializePoolStatisticsThunk());
    }, [dispatch]);

    const ready = !loading && !!statistics;
    const buckets = statistics?.ageBuckets ?? null;
    const unit = statistics?.unit ?? "WEEK";
    const hasData = (statistics?.count ?? 0) > 0;

    const chartStyle = useMemo(() => getChartStyle(isDarkTheme), [isDarkTheme]);
    const colorBar = getPrimaryBarColor(isDarkTheme);
    const colorAvg = getColor(chartStyle.colors[2]);
    const colorMedian = getColor(chartStyle.colors[3]);

    /** Age in days, formatted with one decimal (formatIndex); "–" when null (empty pool). */
    const formatDays = (value: number | null | undefined) => (value == null ? "–" : formatIndex(value, language));

    /** Label of a threshold tile, e.g. "< 1 Woche" / "≥ 2 Monate" (comparator + unit + count). */
    const tileLabel = (tile: PoolThresholdTile) => {
        const prefix = tile.comparator === "LT" ? "< " : "≥ ";
        const unitKey = unit === "WEEK" ? "start.pool.statistics.unitWeek" : "start.pool.statistics.unitMonth";
        return prefix + t(unitKey, {count: tile.unitCount});
    };

    const modeOptions = POOL_STATISTICS_MODES.map((value) => ({value, label: t(modeI18nKey(value))}));

    // Memoized so the chart component's memo sees stable identities — otherwise every unrelated
    // re-render of the tab would destroy and rebuild the chart.js instance.
    const chartData = useMemo<ChartData<"bar">>(() => {
        const source = buckets ?? [];
        // Bucket boundaries are whole days but labeled in the response's unit (WEEK → /7, MONTH → /30);
        // boundaries are exact multiples of the divisor, so the rounding only guards float artifacts.
        const divisor = unit === "WEEK" ? 7 : 30;
        const unitShort =
            unit === "WEEK" ? t("start.pool.statistics.unitShortWeek") : t("start.pool.statistics.unitShortMonth");
        const label = (b: {minDays: number; maxDays: number | null}) => {
            const min = Math.round(b.minDays / divisor);
            return b.maxDays == null
                ? t("start.pool.statistics.bucketOpen", {min, unit: unitShort})
                : t("start.pool.statistics.bucketRange", {min, max: Math.round(b.maxDays / divisor), unit: unitShort});
        };
        const dataset: ChartDataset<"bar"> = {
            label: t("start.pool.statistics.seriesCount"),
            backgroundColor: colorBar,
            borderRadius: 4,
            maxBarThickness: 48,
            data: source.map((b) => b.count),
        };
        return {labels: source.map(label), datasets: [dataset]};
    }, [buckets, unit, colorBar, t, language]);

    // Altersverteilung: wenige feste Buckets, daher alle x-Labels zeigen (kein autoSkip).
    const chartOptions = useMemo<ChartOptions<"bar">>(
        () => buildStatisticsChartOptions(chartStyle, false),
        [chartStyle],
    );

    return (
        <div className={className}>
            <ErrorIndicator titleI18nKey="start.pool.statistics.queryError" errorObject={statisticsError} />
            {/* One row: summary tiles on the left, mode switcher + markers floating right at tile height */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex flex-wrap gap-3">
                    {loading && <LoadingIndicator loading={true} />}
                    {ready && (
                        <>
                            {/* Always: pool size and average age (both mode-independent) */}
                            <StatTile value={String(statistics!.count)} label={t("start.pool.statistics.count")} />
                            <StatTile
                                value={formatDays(statistics!.avgAgeDays)}
                                label={t("start.pool.statistics.avgAge")}
                            />
                            {/* Then the three mode-dependent threshold tiles */}
                            {statistics!.tiles.map((tile) => (
                                <StatTile
                                    key={`${tile.comparator}-${tile.unitCount}`}
                                    value={String(tile.count)}
                                    label={tileLabel(tile)}
                                />
                            ))}
                        </>
                    )}
                </div>
                {/* Right column: mode switcher with the Ø/median markers stacked directly beneath it —
                    together as tall as the summary tiles, so the markers cost no vertical space */}
                <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-2">
                        <SelectButton
                            value={mode}
                            options={modeOptions}
                            allowEmpty={false}
                            onChange={(e) => void dispatch(changePoolStatisticsModeThunk(e.value))}
                        />
                    </div>
                    {ready && hasData && (
                        <div className="flex gap-4">
                            <LegendItem
                                color={colorAvg}
                                label={t("start.pool.statistics.markerAvg", {
                                    value: formatDays(statistics!.avgAgeDays),
                                })}
                            />
                            <LegendItem
                                color={colorMedian}
                                label={t("start.pool.statistics.markerMedian", {
                                    value: formatDays(statistics!.medianAgeDays),
                                })}
                            />
                        </div>
                    )}
                </div>
            </div>
            {ready && hasData && (
                <div className={STATISTICS_CHART_BOX_CLASS}>
                    <Chart type="bar" className="w-full h-full" data={chartData} options={chartOptions} />
                </div>
            )}
            {ready && !hasData && (
                <div
                    className={`${STATISTICS_CHART_BOX_CLASS} flex items-center justify-center
                                text-surface-500 dark:text-surface-300`}
                >
                    {t("start.pool.statistics.empty")}
                </div>
            )}
        </div>
    );
}
